import { useEffect, useState } from "react";

const T = {
  navy: "#0A1E45",
  navy2: "#071530",
  gold: "#C9A540",
  gold2: "#E4C87A",
  white: "#FFFFFF",
  offwhite: "#FAFAF7",
  cream: "#F5F1E8",
  light: "#EEE9DF",
  muted: "#64647A",
  border: "#DDD8CE",
  ffHead: "'Playfair Display', Georgia, serif",
  ffBody: "'DM Sans', system-ui, sans-serif",
};

const CORP_FORM_URL = "https://admissions.eusbd.eu/corporate-apply/";
const CORP_PARTNER_PACK_URL = "/documents/ESBD%20Corporate%20Partner%20Pack%20v1.zip";

const scopedCSS = `
  .corp-wrap { font-family: ${T.ffBody}; font-size: 16px; color: ${T.navy}; background: ${T.white}; -webkit-font-smoothing: antialiased; }
  .corp-wrap * { box-sizing: border-box; }
  .corp-wrap a { color: inherit; }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes slideIn { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
  .corp-wrap .cp-hero-ey { animation: slideIn 0.5s 0.1s ease both; }
  .corp-wrap .cp-hero-h1 { animation: slideIn 0.6s 0.2s ease both; }
  .corp-wrap .cp-hero-sub { animation: slideIn 0.6s 0.3s ease both; }
  .corp-wrap .cp-hero-actions { animation: slideIn 0.6s 0.4s ease both; }
  .corp-wrap .cp-hero-card { animation: fadeUp 0.7s 0.35s ease both; }
  .corp-wrap .cp-model-card { transition: transform 0.2s, box-shadow 0.2s; position: relative; overflow: hidden; }
  .corp-wrap .cp-model-card::before { content: ''; position: absolute; top: 0; left: 0; width: 4px; height: 100%; background: ${T.gold}; transform: scaleY(0); transform-origin: bottom; transition: transform 0.25s; }
  .corp-wrap .cp-model-card:hover::before { transform: scaleY(1); }
  .corp-wrap .cp-model-card:hover { transform: translateY(-3px); box-shadow: 0 12px 40px rgba(10,30,69,0.08); }
  .corp-wrap .cp-impact-card { transition: transform 0.2s; }
  .corp-wrap .cp-impact-card:hover { transform: translateY(-3px); }
  .corp-wrap .cp-faq-btn { width: 100%; text-align: left; background: none; border: none; padding: 22px 0; cursor: pointer; display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; font-family: ${T.ffBody}; font-size: 15px; font-weight: 600; color: ${T.navy}; line-height: 1.45; transition: color 0.15s; }
  .corp-wrap .cp-faq-btn:hover { color: ${T.gold}; }
  @media (max-width: 980px) {
    .corp-wrap .cp-hero-grid { grid-template-columns: 1fr !important; gap: 28px !important; }
    .corp-wrap .cp-model-grid { grid-template-columns: 1fr !important; }
    .corp-wrap .cp-how-grid { grid-template-columns: 1fr !important; }
    .corp-wrap .cp-impact-grid { grid-template-columns: 1fr !important; }
    .corp-wrap .cp-who-grid { grid-template-columns: 1fr !important; }
    .corp-wrap .cp-who-outer { grid-template-columns: 1fr !important; gap: 32px !important; }
    .corp-wrap .cp-faq-grid { grid-template-columns: 1fr !important; gap: 0 !important; }
  }
  @media (max-width: 600px) {
    .corp-wrap > section { padding-left: 20px !important; padding-right: 20px !important; }
    .corp-wrap #apply { padding-left: 20px !important; padding-right: 20px !important; }
    .corp-wrap .cp-hero-grid { gap: 42px !important; }
    .corp-wrap .cp-hero-card { padding: 24px !important; }
    .corp-wrap .cp-trustbar-inner { padding-left: 16px !important; padding-right: 16px !important; }
    .corp-wrap .cp-trustbar-item { padding: 14px 12px 14px 8px !important; }
  }
`;

function useScopedStyles() {
  useEffect(() => {
    const id = "corp-scoped-styles";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = scopedCSS;
    document.head.appendChild(style);
  }, []);
}

const Eyebrow = ({ children, light = false, center = false }) => (
  <div style={{ fontSize: 10, fontWeight: 700, color: light ? T.gold2 : T.gold, letterSpacing: "0.24em", textTransform: "uppercase", marginBottom: 16, textAlign: center ? "center" : "left" }}>
    {children}
  </div>
);

const SectionTitle = ({ children, light = false, center = false }) => (
  <h2 style={{ fontFamily: T.ffHead, fontSize: "clamp(30px, 3.8vw, 46px)", fontWeight: 700, lineHeight: 1.15, color: light ? T.white : T.navy, marginBottom: 16, textAlign: center ? "center" : "left" }}>
    {children}
  </h2>
);

const Lead = ({ children, light = false, center = false }) => (
  <p style={{ fontSize: 17, color: light ? "rgba(255,255,255,0.6)" : T.muted, lineHeight: 1.8, maxWidth: 640, marginBottom: 48, textAlign: center ? "center" : "left", margin: center ? "0 auto 48px" : "0 0 48px" }}>
    {children}
  </p>
);

const TrustBar = () => (
  <div style={{ background: T.gold }}>
    <div
      className="cp-trustbar-inner"
      style={{
        maxWidth: 1200,
        margin: "0 auto",
        padding: "0 40px",
        display: "flex",
        alignItems: "stretch",
        flexWrap: "wrap",
      }}
    >
      {["Preferential Corporate Rates", "Bespoke Executive Education", "Scholarship Sponsorship", "EU Institution", "Online Delivery"].map((item, i, a) => (
        <div
          key={i}
          className="cp-trustbar-item"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "16px 20px 16px 12px",
            borderRight: i < a.length - 1 ? "1px solid rgba(10,30,69,0.15)" : "none",
            fontSize: 11,
            fontWeight: 700,
            color: T.navy,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            flex: "0 0 auto",
          }}
        >
          <span style={{ width: 6, height: 6, background: T.navy, borderRadius: "50%", opacity: 0.4 }} />
          {item}
        </div>
      ))}
    </div>
  </div>
);

const MODELS = [
  { n: "01", title: "Staff Development Discount (12%)", body: "Offer your employees a preferential corporate rate on ESBD programmes. Ideal for HR and L&D teams investing in leadership progression." },
  { n: "02", title: "Sponsored Scholarships", body: "Sponsor scholarships for beneficiaries aligned to your CSR priorities. ESBD administers selection, onboarding and reporting." },
  { n: "03", title: "Bespoke Executive Education", body: "Commission tailored workshops, seminars or cohort programmes. We co-design content to meet your organisational goals." },
];

const IMPACT = [
  { title: "Retention & Engagement", body: "Structured professional development increases employee engagement and retention while building leadership depth." },
  { title: "CSR & Social Impact", body: "Sponsor scholarships and create meaningful impact aligned to your organisation’s values and communities." },
  { title: "Capability Building", body: "Build strategic capability across teams with targeted executive education and applied leadership learning." },
  { title: "Brand Collaboration", body: "Explore co-branded initiatives where appropriate, supported by ESBD guidance and QA frameworks." },
];

const WHO = [
  "Corporations seeking structured staff development",
  "NGOs funding sponsored scholarship programmes",
  "Government bodies investing in leadership capability",
  "Professional institutes and employer associations",
  "Organisations commissioning bespoke CPD and executive education",
];

const FAQS = [
  { q: "How does the 12% staff discount work?", a: "We generate a batch of voucher codes linked to your organisation. Your HR or L&D team distributes these to eligible employees. When an employee applies through the ESBD admissions portal and enters the code, the 12% discount is applied automatically to their programme fee. Codes are valid for one intake cycle." },
  { q: "Is there a minimum number of staff?", a: "No minimum. The staff discount is available from a single employee. However, for cohorts of five or more, we can discuss additional volume arrangements and tailored onboarding support." },
  { q: "Can we sponsor scholarships for people outside our organisation?", a: "Absolutely. Many corporate partners use sponsored scholarships to support beneficiaries in specific communities, countries, or demographics. You set the criteria and funding level, and ESBD handles the selection and administration." },
  { q: "What does bespoke executive education look like?", a: "It depends on your needs. It can be a workshop for senior leadership, a structured programme for middle managers, or a series of seminars. We design the curriculum with you." },
  { q: "Is ESBD a recognised institution?", a: "ESBD is a Malta-based higher education institution currently pursuing MFHEA accreditation. The MA programme is mapped to MQF Level 7 and referenced to the European Qualifications Framework." },
  { q: "Do employees have to study full-time?", a: "No. The programme is designed for working professionals. Content is delivered online through the ESBD learning platform with structured learning activities and webinars." },
  { q: "How does the corporate partnership differ from the SCP model?", a: "The Study Centre Partner model is commission-based for education agencies who bring external students. The Corporate Partnership is designed for organisations sending their own staff or sponsoring beneficiaries — there is no commission; the value is in discounted fees, bespoke programmes, and collaboration." },
  { q: "What reporting do we receive?", a: "For staff development and sponsored scholarships, we provide periodic progress summaries including enrolment status and module completion. All reporting respects GDPR." },
];

export default function CorporatePartnershipPage() {
  useScopedStyles();
  const [open, setOpen] = useState(null);
  const half = Math.ceil(FAQS.length / 2);

  return (
    <div className="corp-wrap">
      <section style={{ background: T.navy, position: "relative", overflow: "hidden", padding: "100px 32px 90px" }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 50% 70% at 75% 50%, rgba(201,165,64,0.06) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: -40, right: -40, width: 360, height: 360, border: "1px solid rgba(201,165,64,0.1)", borderRadius: "50%", pointerEvents: "none" }} />

        <div className="cp-hero-grid" style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 1, display: "grid", gridTemplateColumns: "1fr 400px", gap: 80, alignItems: "center" }}>
          <div>
            <div className="cp-hero-ey"><Eyebrow light>Corporate Partnerships</Eyebrow></div>
            <h1 className="cp-hero-h1" style={{ fontFamily: T.ffHead, fontSize: "clamp(36px, 5vw, 54px)", fontWeight: 900, lineHeight: 1.08, color: T.white, marginBottom: 28 }}>
              Invest in Your People.
              <br />
              <em style={{ color: T.gold, fontStyle: "italic", display: "block" }}>Partner with a European Institution.</em>
            </h1>
            <p className="cp-hero-sub" style={{ fontSize: 17, color: "rgba(255,255,255,0.7)", lineHeight: 1.75, maxWidth: 500, marginBottom: 40 }}>
              Give your team access to a Malta-based, MFHEA-regulated master&apos;s degree at a preferential corporate rate. Or go further:
              sponsor scholarships, commission bespoke executive education, or collaborate on leadership development initiatives.
            </p>
            <div className="cp-hero-actions" style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <a href="#apply" style={{ background: T.gold, color: T.navy, fontFamily: T.ffBody, fontSize: 14, fontWeight: 700, padding: "14px 32px", borderRadius: 3, textDecoration: "none", letterSpacing: "0.05em", textTransform: "uppercase", border: `2px solid ${T.gold}` }}>
                Express Interest
              </a>
              <a href={CORP_PARTNER_PACK_URL} download="ESBD Corporate Partner Pack v1.zip" style={{ background: "transparent", color: "rgba(255,255,255,0.85)", fontFamily: T.ffBody, fontSize: 14, fontWeight: 500, padding: "14px 32px", borderRadius: 3, textDecoration: "none", border: "1px solid rgba(255,255,255,0.25)" }}>
                Download Partner Pack
              </a>
            </div>
          </div>

          <div className="cp-hero-card" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(201,165,64,0.25)", padding: 36, backdropFilter: "blur(8px)" }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: T.gold, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 18, paddingBottom: 16, borderBottom: "1px solid rgba(201,165,64,0.2)" }}>
              Partnership at a glance
            </div>
            {[
              { num: "12%", label: "Staff Discount" },
              { num: "CSR", label: "Sponsored Scholarships" },
              { num: "CPD", label: "Bespoke Executive Education" },
              { num: "EU", label: "Malta-Based Institution" },
            ].map(({ num, label }, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", padding: "16px 0", borderBottom: i < 3 ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                <span style={{ fontFamily: T.ffHead, fontSize: 34, fontWeight: 700, color: T.gold, lineHeight: 1, marginBottom: 4 }}>{num}</span>
                <span style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TrustBar />

      <section id="models" style={{ padding: "100px 40px", background: T.white }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Eyebrow>Partnership Models</Eyebrow>
          <SectionTitle>Choose the right collaboration structure</SectionTitle>
          <Lead>Flexible partnership routes to support staff development, scholarships, and leadership capability building.</Lead>

          <div className="cp-model-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}>
            {MODELS.map(({ n, title, body }) => (
              <div key={n} className="cp-model-card" style={{ background: T.white, border: `1px solid ${T.border}`, padding: "36px 30px" }}>
                <div style={{ fontFamily: T.ffHead, fontSize: 56, fontWeight: 900, color: T.cream, lineHeight: 1, marginBottom: -8 }}>{n}</div>
                <div style={{ fontFamily: T.ffHead, fontSize: 18, fontWeight: 700, color: T.navy, marginBottom: 12, lineHeight: 1.3 }}>{title}</div>
                <div style={{ fontSize: 14, color: T.muted, lineHeight: 1.75 }}>{body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="how" style={{ padding: "100px 40px", background: T.navy }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Eyebrow light>How It Works</Eyebrow>
          <SectionTitle light>A simple, collaborative process</SectionTitle>
          <Lead light>Tell us what you need. We propose a partnership model, confirm scope, and activate your collaboration for the next intake cycle.</Lead>

          <div className="cp-how-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
            {[
              { n: "01", t: "Express Interest", b: "Share your organisation details and what you aim to achieve with ESBD." },
              { n: "02", t: "Review & Design", b: "We align partnership models, reporting needs, and any bespoke elements." },
              { n: "03", t: "Launch", b: "Activate your staff discount, scholarship sponsorship, or executive education plan." },
            ].map((s) => (
              <div key={s.n} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", padding: 28 }}>
                <div style={{ fontFamily: T.ffHead, fontSize: 44, fontWeight: 900, color: "rgba(255,255,255,0.06)", lineHeight: 1, marginBottom: 10 }}>{s.n}</div>
                <div style={{ fontFamily: T.ffHead, fontSize: 20, fontWeight: 700, color: T.white, marginBottom: 10 }}>{s.t}</div>
                <div style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", lineHeight: 1.8 }}>{s.b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="impact" style={{ padding: "100px 40px", background: T.cream }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Eyebrow>Impact</Eyebrow>
          <SectionTitle>Outcomes you can measure</SectionTitle>
          <Lead>Partnerships are designed to support measurable organisational outcomes while maintaining quality and governance.</Lead>

          <div className="cp-impact-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
            {IMPACT.map((x) => (
              <div key={x.title} className="cp-impact-card" style={{ background: T.white, border: `1px solid ${T.border}`, padding: "26px 22px" }}>
                <div style={{ fontFamily: T.ffHead, fontSize: 16, fontWeight: 700, color: T.navy, marginBottom: 8 }}>{x.title}</div>
                <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.7 }}>{x.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="who" style={{ padding: "100px 40px", background: T.white }}>
        <div className="cp-who-outer" style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60 }}>
          <div>
            <Eyebrow>Who It&apos;s For</Eyebrow>
            <SectionTitle>Organisations that invest in people</SectionTitle>
            <Lead>We work with organisations seeking structured staff development, scholarship sponsorship, and leadership capability building.</Lead>
          </div>
          <div className="cp-who-grid" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10, alignContent: "start" }}>
            {WHO.map((w) => (
              <div key={w} style={{ border: `1px solid ${T.border}`, background: T.offwhite, padding: "16px 18px", display: "flex", gap: 12, alignItems: "flex-start" }}>
                <span style={{ width: 20, height: 20, background: T.navy, color: T.gold, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, flexShrink: 0, marginTop: 2 }}>✓</span>
                <div style={{ fontSize: 14, color: T.navy, lineHeight: 1.6 }}>{w}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="faq" style={{ padding: "100px 40px", background: T.cream }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Eyebrow center>FAQs</Eyebrow>
          <SectionTitle center>Common questions about corporate partnerships</SectionTitle>
          <div style={{ height: 48 }} />
          <div className="cp-faq-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 60px" }}>
            {[FAQS.slice(0, half), FAQS.slice(half)].map((col, ci) => (
              <div key={ci}>
                {col.map((item, i) => {
                  const idx = ci * half + i;
                  return (
                    <div key={idx} style={{ borderBottom: `1px solid ${T.border}` }}>
                      <button className="cp-faq-btn" onClick={() => setOpen(open === idx ? null : idx)}>
                        <span>{item.q}</span>
                        <span style={{ width: 24, height: 24, background: open === idx ? T.gold : T.navy, color: open === idx ? T.navy : T.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0, transition: "background 0.2s" }}>{open === idx ? "−" : "+"}</span>
                      </button>
                      {open === idx && <div style={{ padding: "0 0 20px", fontSize: 14, color: T.muted, lineHeight: 1.8 }}>{item.a}</div>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </section>

      <div id="apply" style={{ background: T.navy, padding: "80px 40px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 60% 100% at 50% 100%, rgba(201,165,64,0.08), transparent)", pointerEvents: "none" }} />
        <h2 style={{ fontFamily: T.ffHead, fontSize: 40, fontWeight: 700, color: T.white, marginBottom: 14, position: "relative" }}>Ready to Explore a Corporate Partnership?</h2>
        <p style={{ fontSize: 17, color: "rgba(255,255,255,0.6)", marginBottom: 36, position: "relative" }}>Tell us about your organisation and what you are looking for. Our Partnerships team will respond within five working days.</p>
        <a href={CORP_FORM_URL} style={{ display: "inline-block", background: T.gold, color: T.navy, padding: "16px 48px", fontSize: 14, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", textDecoration: "none", position: "relative", transition: "all 0.25s" }}>
          Express Interest — Corporate Partnership →
        </a>
      </div>
    </div>
  );
}

